import React, { useEffect, useCallback, useReducer, useRef } from 'react';
import { Loader, Icon } from '@Sage/hoi-poi-ui';

import Context from 'managers/Context';
import { OPPORTUNITIES } from 'constants/Entities';
import { formatSelectionFilters } from 'domain/FilterApiWrapper';
import { getLiteral } from 'utils/getLiteral';
import { subscribe } from 'lib/EventBuser';
import { ENTITY_MODAL_UPDATE } from 'lib/events';

import Scrollbar from 'components/ScrollBar';
import { WidgetEmpty } from 'containers/components/widgets/Layouts';
import SizeComponent from 'components/SizeComponent';

import WidgetOpportunitiesOpportunityRow from './WidgetOpportunitiesOpportunityRow';

const PAGE_SIZE = 50;

function reducer(state, action) {
    switch (action.type) {
        case 'loading':
            return { ...state, loading: action.payload };
        case 'isLast':
            return { ...state, isLast: action.payload };
        case 'opportunities':
            return {
                ...state,
                opportunities: action.withReset
                    ? [...action.payload]
                    : [...state.opportunities, ...action.payload],
            };
        default:
            return state;
    }
}

const WidgetOpportunitiesList = ({ entityType, entityId, updateOpportunitiesCount, setSize }) => {
    const [state, dispatch] = useReducer(reducer, {
        loading: false,
        isLast: false,
        opportunities: [],
    });

    const offsetRef = useRef(0);
    const loadingRef = useRef(false);
    const isLastRef = useRef(false);
    const requestGenerationRef = useRef(0);

    const getOpportunitiesCount = useCallback(() => {
        if (!entityId) return;

        const filters = {
            [entityType.entity]: {
                dataType: 'singleValueList',
                id: entityType.entity,
                value: [{ Id: entityId }],
            },
        };
        const params = { filter: formatSelectionFilters(filters, OPPORTUNITIES) };

        Context.entityListManager
            .getEntityCounts(OPPORTUNITIES.countName, params)
            .then((total) => updateOpportunitiesCount?.(total));
    }, [entityId, entityType.entity, updateOpportunitiesCount]);

    const getOpportunities = useCallback(
        (withReset = false) => {
            if (withReset) {
                offsetRef.current = 0;
                isLastRef.current = false;
                requestGenerationRef.current += 1;
            } else if (loadingRef.current || isLastRef.current) {
                return;
            }

            const requestGeneration = requestGenerationRef.current;
            const requestOffset = withReset ? 0 : offsetRef.current;
            const shouldReplace = withReset;

            loadingRef.current = true;
            const filter = {
                [entityType.entity]: {
                    dataType: 'singleValueList',
                    id: entityType.entity,
                    value: [{ Id: entityId }],
                },
            };

            dispatch({ type: 'loading', payload: true });
            Context.entityListManager.getEntityList(
                OPPORTUNITIES,
                requestOffset,
                PAGE_SIZE,
                filter,
                'fechacierre',
                '1',
                null,
                false,
                (data) => {
                    if (requestGeneration !== requestGenerationRef.current) return;

                    const isLast = !data.length || data.length < PAGE_SIZE;
                    isLastRef.current = isLast;
                    loadingRef.current = false;
                    offsetRef.current = requestOffset + PAGE_SIZE;

                    dispatch({ type: 'loading', payload: false });
                    dispatch({ type: 'opportunities', payload: data, withReset: shouldReplace });
                    dispatch({ type: 'isLast', payload: isLast });
                },
                () => {
                    if (requestGeneration !== requestGenerationRef.current) return;

                    isLastRef.current = true;
                    loadingRef.current = false;
                    dispatch({ type: 'loading', payload: false });
                    dispatch({ type: 'isLast', payload: true });
                },
                false,
                true,
            );
        },
        [entityId, entityType.entity],
    );

    useEffect(() => {
        if (!entityId) return;

        getOpportunitiesCount();
        getOpportunities(true);

        return subscribe(`${ENTITY_MODAL_UPDATE}--${OPPORTUNITIES.entity}`, () => {
            getOpportunitiesCount();
            getOpportunities(true);
        });
    }, [entityId, entityType.entity, getOpportunities, getOpportunitiesCount]);

    const memoizedSetSize = useCallback(
        (size) => {
            setSize &&
                setSize({
                    ...size,
                    height: size.height + 40,
                });
        },
        [setSize],
    );

    const handleScrollFrame = useCallback(
        (values) => {
            if (isLastRef.current || loadingRef.current) return;
            if (values.top < 0.95) return;
            getOpportunities();
        },
        [getOpportunities],
    );

    const isEmpty = state.opportunities.length === 0;
    if (isEmpty) {
        return (
            <SizeComponent setSize={memoizedSetSize}>
                {state.loading && <WidgetEmpty icon={<Loader />} />}
                {!state.loading && (
                    <WidgetEmpty
                        icon={<Icon name="opportunities" size="large" />}
                        text={getLiteral('error_norelatedopportunities')}
                    />
                )}
            </SizeComponent>
        );
    }

    return (
        <Scrollbar
            autoHide={true}
            hideTracksWhenNotNeeded={true}
            style={{ height: '100%' }}
            onScrollFrame={handleScrollFrame}
        >
            <SizeComponent setSize={memoizedSetSize}>
                <div className="fm-widget-opportunities-list">
                    {state.opportunities.map((opportunity) => (
                        <WidgetOpportunitiesOpportunityRow
                            key={opportunity.Id}
                            opportunity={opportunity}
                            entityType={entityType}
                        />
                    ))}
                </div>
            </SizeComponent>
        </Scrollbar>
    );
};

export default WidgetOpportunitiesList;
